/**
 * PWA Hook - React hook for managing PWA functionality
 * Provides easy access to PWA features, installation, updates, and offline status
 */

import { useState, useEffect, useCallback } from 'react';
import { pwaService, PWAUpdateInfo, PWAInstallInfo, PWAOfflineInfo, PWAPerformanceInfo } from '@/services/pwa.service';
import { offlineService } from '@/services/offline.service';

interface PWAState {
  isSupported: boolean;
  isInitialized: boolean;
  isOnline: boolean;
  isInstallable: boolean;
  isInstalled: boolean;
  isUpdateAvailable: boolean;
  isRefreshing: boolean;
  installInfo: PWAInstallInfo | null;
  updateInfo: PWAUpdateInfo | null;
  networkInfo: PWAOfflineInfo | null;
  performanceInfo: PWAPerformanceInfo | null;
  syncStatus: 'idle' | 'syncing' | 'completed' | 'failed';
  pendingSyncCount: number;
}

interface PWAActions {
  initialize: () => Promise<void>;
  install: () => Promise<void>;
  update: () => Promise<void>;
  refresh: () => Promise<void>;
  clearCache: () => Promise<void>;
  forceSync: () => Promise<void>;
  getPerformanceData: () => Promise<void>;
}

interface PWACapabilities {
  serviceWorker: boolean;
  cache: boolean;
  fetch: boolean;
  notification: boolean;
  geolocation: boolean;
  deviceMotion: boolean;
  deviceOrientation: boolean;
  bluetooth: boolean;
  webShare: boolean;
  webgl: boolean;
  webrtc: boolean;
  mediaDevices: boolean;
}

const initialState: PWAState = {
  isSupported: false,
  isInitialized: false,
  isOnline: navigator.onLine,
  isInstallable: false,
  isInstalled: false,
  isUpdateAvailable: false,
  isRefreshing: false,
  installInfo: null,
  updateInfo: null,
  networkInfo: null,
  performanceInfo: null,
  syncStatus: 'idle',
  pendingSyncCount: 0
};

export const usePWA = () => {
  const [state, setState] = useState<PWAState>(initialState);
  const [capabilities, setCapabilities] = useState<PWACapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize PWA services
  const initialize = useCallback(async () => {
    if (state.isInitialized) return;

    try {
      setError(null);
      
      // Check PWA support
      const isSupported = pwaService.isSupported();
      if (!isSupported) {
        setState(prev => ({ ...prev, isSupported: false }));
        return;
      }

      // Initialize PWA service
      await pwaService.initialize();
      
      // Initialize offline service
      await offlineService.initialize();

      // Get initial state
      const installInfo = pwaService.getInstallInfo();
      const networkInfo = pwaService.getNetworkInfo();
      const capabilitiesInfo = pwaService.getCapabilities();
      
      setState(prev => ({
        ...prev,
        isSupported: true,
        isInitialized: true,
        isInstallable: installInfo.isInstallable,
        isInstalled: installInfo.isInstalled,
        installInfo,
        networkInfo
      }));

      setCapabilities(capabilitiesInfo);

      console.log('✅ PWA hook initialized successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize PWA';
      setError(errorMessage);
      console.error('❌ PWA initialization failed:', err);
    }
  }, [state.isInitialized]);

  // Install PWA
  const install = useCallback(async () => {
    try {
      setError(null);
      
      if (!state.installInfo?.isInstallable) {
        throw new Error('PWA installation not available');
      }

      await state.installInfo.install();
      
      // Update state
      const installInfo = pwaService.getInstallInfo();
      setState(prev => ({
        ...prev,
        isInstallable: installInfo.isInstallable,
        isInstalled: installInfo.isInstalled,
        installInfo
      }));

      console.log('✅ PWA installed successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to install PWA';
      setError(errorMessage);
      console.error('❌ PWA installation failed:', err);
      throw err;
    }
  }, [state.installInfo]);

  // Update PWA
  const update = useCallback(async () => {
    try {
      setError(null);
      
      if (!state.updateInfo?.isUpdateAvailable) {
        throw new Error('No update available');
      }

      await state.updateInfo.skipWaiting();
      
      setState(prev => ({
        ...prev,
        isUpdateAvailable: false,
        updateInfo: null
      }));

      console.log('✅ PWA updated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update PWA';
      setError(errorMessage);
      console.error('❌ PWA update failed:', err);
      throw err;
    }
  }, [state.updateInfo]);

  // Refresh app data
  const refresh = useCallback(async () => {
    try {
      setError(null);
      setState(prev => ({ ...prev, isRefreshing: true }));

      // Force sync with server
      await forceSync();
      
      // Get fresh performance data
      await getPerformanceData();

      setState(prev => ({ ...prev, isRefreshing: false }));
      console.log('✅ App refreshed successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh app';
      setError(errorMessage);
      setState(prev => ({ ...prev, isRefreshing: false }));
      console.error('❌ App refresh failed:', err);
      throw err;
    }
  }, []);

  // Clear all caches
  const clearCache = useCallback(async () => {
    try {
      setError(null);
      
      await pwaService.clearCaches();
      await offlineService.clearAllData();
      
      // Update performance data
      await getPerformanceData();
      
      console.log('✅ Cache cleared successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear cache';
      setError(errorMessage);
      console.error('❌ Cache clearing failed:', err);
      throw err;
    }
  }, []);

  // Force sync with server
  const forceSync = useCallback(async () => {
    try {
      setError(null);
      setState(prev => ({ ...prev, syncStatus: 'syncing' }));

      // This would need to be connected to your API service
      // For now, we'll simulate the sync
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setState(prev => ({ 
        ...prev, 
        syncStatus: 'completed',
        pendingSyncCount: 0
      }));

      console.log('✅ Sync completed successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync data';
      setError(errorMessage);
      setState(prev => ({ ...prev, syncStatus: 'failed' }));
      console.error('❌ Sync failed:', err);
      throw err;
    }
  }, []);

  // Get performance data
  const getPerformanceData = useCallback(async () => {
    try {
      const performanceInfo = await pwaService.getPerformanceInfo();
      const pendingSyncCount = await offlineService.getPendingSyncCount();
      
      setState(prev => ({
        ...prev,
        performanceInfo,
        pendingSyncCount
      }));
    } catch (err) {
      console.error('❌ Failed to get performance data:', err);
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (!state.isInitialized) return;

    // Listen for update notifications
    const unsubscribeUpdate = pwaService.onUpdateAvailable((updateInfo) => {
      setState(prev => ({
        ...prev,
        isUpdateAvailable: true,
        updateInfo
      }));
    });

    // Listen for offline status changes
    const unsubscribeOffline = pwaService.onOfflineChange((isOffline) => {
      setState(prev => ({
        ...prev,
        isOnline: !isOffline
      }));
    });

    // Listen for sync status changes
    const unsubscribeSync = offlineService.onSyncStatus((status, progress) => {
      setState(prev => ({
        ...prev,
        syncStatus: status as any
      }));
    });

    return () => {
      unsubscribeUpdate();
      unsubscribeOffline();
      unsubscribeSync();
    };
  }, [state.isInitialized]);

  // Update network info periodically
  useEffect(() => {
    if (!state.isInitialized) return;

    const updateNetworkInfo = () => {
      const networkInfo = pwaService.getNetworkInfo();
      setState(prev => ({ ...prev, networkInfo }));
    };

    // Update immediately
    updateNetworkInfo();

    // Update every 30 seconds
    const interval = setInterval(updateNetworkInfo, 30000);

    return () => clearInterval(interval);
  }, [state.isInitialized]);

  // Update performance data periodically
  useEffect(() => {
    if (!state.isInitialized) return;

    const updatePerformanceData = async () => {
      await getPerformanceData();
    };

    // Update immediately
    updatePerformanceData();

    // Update every 5 minutes
    const interval = setInterval(updatePerformanceData, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [state.isInitialized, getPerformanceData]);

  // PWA actions
  const actions: PWAActions = {
    initialize,
    install,
    update,
    refresh,
    clearCache,
    forceSync,
    getPerformanceData
  };

  // Helper functions
  const isStandalone = state.installInfo?.platform === 'standalone';
  const canInstall = state.isInstallable && !state.isInstalled;
  const needsUpdate = state.isUpdateAvailable;
  const isOffline = !state.isOnline;
  const hasOfflineData = state.pendingSyncCount > 0;

  // Connection quality helper
  const getConnectionQuality = (): 'excellent' | 'good' | 'poor' | 'offline' => {
    if (!state.isOnline) return 'offline';
    
    const effectiveType = state.networkInfo?.effectiveType;
    switch (effectiveType) {
      case '4g':
        return 'excellent';
      case '3g':
        return 'good';
      case '2g':
      case 'slow-2g':
        return 'poor';
      default:
        return 'good';
    }
  };

  // Performance status helper
  const getPerformanceStatus = (): 'excellent' | 'good' | 'poor' => {
    if (!state.performanceInfo) return 'good';
    
    const cacheHitRatio = state.performanceInfo.cacheHits / 
      (state.performanceInfo.cacheHits + state.performanceInfo.networkRequests);
    
    if (cacheHitRatio > 0.8) return 'excellent';
    if (cacheHitRatio > 0.6) return 'good';
    return 'poor';
  };

  return {
    // State
    ...state,
    capabilities,
    error,
    
    // Actions
    ...actions,
    
    // Helpers
    isStandalone,
    canInstall,
    needsUpdate,
    isOffline,
    hasOfflineData,
    connectionQuality: getConnectionQuality(),
    performanceStatus: getPerformanceStatus(),
    
    // Clear error
    clearError: () => setError(null)
  };
};

export default usePWA;